// Filter der Berichte in der Adresse (Agent 7A): Zeitraum, Vergleich, Nutzer, Smart View, geöffneter gespeicherter Bericht.
// So lässt sich jede Ansicht als Link teilen, und die Unterseiten behalten die Auswahl beim Wechseln.
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { BerichtEingabe, Vergleich } from "../api/berichte";
import { heute } from "../werkzeuge/zeit";
import { istZeitraumArt, tageFuer, type Tage, type ZeitraumArt, type ZeitraumWahl } from "./zeitraum";

export interface FilterWerte {
  wahl: ZeitraumWahl;
  vergleich: Vergleich | null;
  nutzerIds: string[];
  smartViewId: string | null;
  berichtId: string | null;
}

const TAG = /^\d{4}-\d{2}-\d{2}$/;

export function werteAusSuche(sp: URLSearchParams, standard: ZeitraumArt): FilterWerte {
  const z = sp.get("z");
  let wahl: ZeitraumWahl = { art: standard };
  if (z === "eigen") {
    const von = sp.get("von");
    const bis = sp.get("bis");
    if (von && TAG.test(von) && bis && TAG.test(bis)) wahl = { art: "eigen", von, bis };
  } else if (istZeitraumArt(z) && z !== "eigen") wahl = { art: z };
  const v = sp.get("v");
  return {
    wahl,
    vergleich: v === "vorperiode" || v === "vorjahr" ? v : null,
    nutzerIds: (sp.get("u") ?? "").split(",").filter(Boolean),
    smartViewId: sp.get("sv") || null,
    berichtId: sp.get("b") || null,
  };
}

/** Teile der Adresse, die die Unterseiten beim Wechseln mitnehmen (alles außer dem gespeicherten Bericht). */
export function suchparameterBehalten(suche: string): string {
  const sp = new URLSearchParams(suche);
  sp.delete("b");
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export function useBerichtFilter(standard: ZeitraumArt = "diese_woche") {
  const [sp, setSp] = useSearchParams();
  const werte = useMemo(() => werteAusSuche(sp, standard), [sp, standard]);
  const tage: Tage = useMemo(() => tageFuer(werte.wahl, heute()), [werte.wahl]);

  /** Felder ändern; `undefined` lässt ein Feld unberührt. Ein Wechsel des Filters löst den gespeicherten Bericht nicht, er macht ihn „geändert“. */
  const setze = useCallback(
    (patch: Partial<FilterWerte>, ersetzen = false) => {
      const neu = { ...werte, ...patch };
      const n = new URLSearchParams();
      if (neu.wahl.art !== standard || neu.wahl.art === "eigen") n.set("z", neu.wahl.art);
      if (neu.wahl.art === "eigen") {
        n.set("von", neu.wahl.von ?? heute());
        n.set("bis", neu.wahl.bis ?? neu.wahl.von ?? heute());
      }
      if (neu.vergleich) n.set("v", neu.vergleich);
      if (neu.nutzerIds.length) n.set("u", neu.nutzerIds.join(","));
      if (neu.smartViewId) n.set("sv", neu.smartViewId);
      if (neu.berichtId) n.set("b", neu.berichtId);
      setSp(n, { replace: ersetzen });
    },
    [werte, standard, setSp],
  );

  const eingabe: BerichtEingabe = useMemo(
    () => ({
      von: tage.von,
      bis: tage.bis,
      ...(werte.nutzerIds.length ? { nutzer_ids: werte.nutzerIds } : {}),
      ...(werte.smartViewId ? { smart_view_id: werte.smartViewId } : {}),
      ...(werte.vergleich ? { vergleich: werte.vergleich } : {}),
    }),
    [tage, werte.nutzerIds, werte.smartViewId, werte.vergleich],
  );
  const eingabeSchluessel = JSON.stringify(eingabe);
  return { werte, tage, setze, eingabe, eingabeSchluessel, suche: sp.toString() };
}
